import React, { useState, useEffect } from 'react';
import { 
    Table, 
    Button, 
    Space, 
    Tag, 
    Badge, 
    message, 
    Popconfirm,
    Select,
    Row,
    Col,
    Card,
    Statistic,
    Modal,
    Form,
    Input,
    DatePicker,
    Alert,
    Descriptions,
    Tooltip
} from 'antd';
import { 
    PlusOutlined, 
    EyeOutlined, 
    DeleteOutlined,
    ReloadOutlined,
    SyncOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import settlementApi from '../../../api/settlementApi';
import { BatchStatusBadge, BatchTypeTag } from './components';

const { Option } = Select;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

/**
 * 批次核算列表页
 */
const BatchCalculationListPage = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [dataSource, setDataSource] = useState([]);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20,
        total: 0
    });
    const [filters, setFilters] = useState({
        type: undefined,
        status: undefined,
        aircraft_reg: undefined
    });
    const [modalVisible, setModalVisible] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [form] = Form.useForm();
    const [expandedChildren, setExpandedChildren] = useState({});
    const [loadingChildren, setLoadingChildren] = useState({});
    const [aircraftOptions, setAircraftOptions] = useState([]);

    // 加载数据
    const loadData = async (page = pagination.current, pageSize = pagination.pageSize) => {
        try {
            setLoading(true);
            const params = {
                page,
                page_size: pageSize,
                type: filters.type,
                status: filters.status,
                aircraft_reg: filters.aircraft_reg
            };

            const response = await settlementApi.batchCalculations.getBatchCalculations(params);
            
            setDataSource(response.data.items || []);
            setPagination({
                current: response.data.page,
                pageSize: response.data.page_size,
                total: response.data.total
            });
        } catch (error) {
            console.error('加载批次列表失败:', error);
            message.error('加载批次列表失败: ' + (error.response?.data?.detail || error.message));
        } finally {
            setLoading(false);
        }
    };

    const loadChildren = async (parentBatchId) => {
        if (expandedChildren[parentBatchId]) return;
        try {
            setLoadingChildren(prev => ({ ...prev, [parentBatchId]: true }));
            const response = await settlementApi.batchCalculations.getBatchCalculation(parentBatchId);
            const children = response.data.children || [];
            setExpandedChildren(prev => ({ ...prev, [parentBatchId]: children }));
        } catch (error) {
            console.error('加载子批次失败:', error);
            message.error('加载子批次失败');
        } finally {
            setLoadingChildren(prev => ({ ...prev, [parentBatchId]: false }));
        }
    };

    useEffect(() => {
        loadData(1);
    }, [filters]);

    useEffect(() => {
        settlementApi.routeInstances.getAircraftList()
            .then(res => {
                const data = res.data;
                setAircraftOptions(Array.isArray(data) ? data : (data?.items || data?.data || []));
            })
            .catch(() => {});
    }, []);

    // 删除批次
    const handleDelete = async (id) => {
        try {
            await settlementApi.batchCalculations.deleteBatchCalculation(id);
            message.success('批次删除成功');
            loadData();
        } catch (error) {
            console.error('删除批次失败:', error);
            message.error('删除批次失败: ' + (error.response?.data?.detail || error.message));
        }
    };

    // 查看详情
    const handleView = (record) => {
        navigate(`/settlement/batch-calculations/${record.batch_id}`);
    };

    // 刷新批次
    const handleRefresh = (record) => {
        Modal.confirm({
            title: '确认刷新批次',
            content: (
                <div>
                    <p>刷新批次将删除旧批次并重新创建，包括：</p>
                    <ul>
                        <li>重新查询航班数据</li>
                        <li>重新执行航线匹配</li>
                        <li>重新聚合成本</li>
                        <li>更新汇率快照</li>
                    </ul>
                    <p style={{ color: '#ff4d4f', marginTop: 8 }}>
                        <strong>注意：</strong>批次ID会变化（分配新ID），但名称、类型、时间范围等配置信息保持不变。
                    </p>
                    <p>确定要刷新批次 "<strong>{record.batch_name}</strong>" 吗？</p>
                </div>
            ),
            okText: '确认刷新',
            cancelText: '取消',
            width: 600,
            onOk: async () => {
                try {
                    message.loading({ content: '正在刷新批次（删除重建中）...', key: 'refresh', duration: 0 });
                    
                    const response = await settlementApi.batchCalculations.refreshBatchCalculation(record.batch_id);
                    const newBatchId = response.data.batch_id;
                    
                    message.success({ 
                        content: `批次已重建（新ID: ${newBatchId}），正在后台计算...`, 
                        key: 'refresh',
                        duration: 3
                    });
                    
                    // 立即刷新列表
                    loadData();
                    
                    // 开始轮询新批次状态
                    startBatchPolling(newBatchId, record.batch_name);
                    
                } catch (error) {
                    console.error('刷新批次失败:', error);
                    const errorMsg = error.response?.data?.detail?.message || error.message || '刷新失败';
                    message.error({ content: `刷新批次失败: ${errorMsg}`, key: 'refresh' });
                }
            }
        });
    };

    // 创建批次
    const handleCreate = () => {
        form.resetFields();
        setModalVisible(true);
    };

    // 提交创建表单
    const handleSubmit = async (values, replaceExisting = false) => {
        try {
            setSubmitting(true);

            // 格式化日期
            const data = {
                batch_name: values.batch_name,
                type: values.type,
                start_date: values.date_range[0].format('YYYY-MM-DD'),
                end_date: values.date_range[1].format('YYYY-MM-DD'),
                description: values.description || undefined,
                replace_existing: replaceExisting
            };

            message.loading({ content: '正在创建批次...', key: 'batch', duration: 0 });

            const response = await settlementApi.batchCalculations.createBatchCalculation(data);
            
            message.success({ 
                content: '批次已创建，正在后台处理...', 
                key: 'batch',
                duration: 3
            });
            
            setModalVisible(false);
            form.resetFields();
            
            // 立即刷新列表（会显示RUNNING状态的批次）
            loadData();

            // 开始轮询批次状态
            const batchId = response.data.batch_id;
            startBatchPolling(batchId, values.batch_name);

        } catch (error) {
            console.error('创建批次失败:', error);
            
            // 处理批次冲突
            if (error.response?.status === 409) {
                handleBatchConflict(error.response.data.detail, values);
                return;
            }
            
            const errorMsg = error.response?.data?.detail?.message || error.message || '创建失败';
            message.error({ content: `创建批次失败: ${errorMsg}`, key: 'batch' });
        } finally {
            setSubmitting(false);
        }
    };

    // 开始轮询批次状态
    const startBatchPolling = (batchId, batchName) => {
        let pollCount = 0;
        const maxPolls = 120; // 最多轮询10分钟（每5秒一次）
        
        message.loading({ 
            content: `批次 "${batchName}" 正在处理中...`, 
            key: `polling_${batchId}`, 
            duration: 0 
        });

        const pollInterval = setInterval(async () => {
            try {
                pollCount++;
                const statusResponse = await settlementApi.batchCalculations.getBatchCalculationStatus(batchId);
                const status = statusResponse.data.status;
                
                console.log(`批次 ${batchId} 状态: ${status} (第${pollCount}次查询)`);

                if (status === 'COMPLETED') {
                    // 处理完成
                    clearInterval(pollInterval);
                    message.success({ 
                        content: `批次 "${batchName}" 处理完成！`, 
                        key: `polling_${batchId}`,
                        duration: 4
                    });
                    
                    // 刷新列表显示最新状态
                    loadData();
                    
                    // 显示完成通知，询问是否跳转到详情页
                    setTimeout(() => {
                        message.info({
                            content: (
                                <span>
                                    批次处理完成，<a onClick={() => navigate(`/settlement/batch-calculations/${batchId}`)}>点击查看详情</a>
                                </span>
                            ),
                            duration: 8
                        });
                    }, 1000);
                    
                } else if (status === 'FAILED') {
                    // 处理失败
                    clearInterval(pollInterval);
                    const errorMsg = statusResponse.data.error_message || '未知错误';
                    message.error({ 
                        content: `批次 "${batchName}" 处理失败: ${errorMsg}`, 
                        key: `polling_${batchId}`,
                        duration: 8
                    });
                    
                    // 刷新列表显示失败状态
                    loadData();
                    
                } else if (status === 'NEEDS_RECALCULATION') {
                    // 需要重新计算（数据源已变更）
                    clearInterval(pollInterval);
                    message.warning({ 
                        content: `批次 "${batchName}" 因数据源变更，需要重新计算`, 
                        key: `polling_${batchId}`,
                        duration: 6
                    });
                    
                    // 刷新列表显示状态
                    loadData();
                    
                } else if (pollCount >= maxPolls) {
                    // 超时停止轮询
                    clearInterval(pollInterval);
                    message.warning({ 
                        content: `批次 "${batchName}" 处理时间较长，请稍后手动刷新查看结果`, 
                        key: `polling_${batchId}`,
                        duration: 6
                    });
                    
                } else if (status === 'RUNNING') {
                    // 继续轮询，更新进度信息
                    const progress = statusResponse.data.progress;
                    if (progress) {
                        message.loading({ 
                            content: `批次 "${batchName}" 处理中... (已用时: ${progress.elapsed_formatted})`, 
                            key: `polling_${batchId}`, 
                            duration: 0 
                        });
                    }
                    
                    // 每10次轮询刷新一次列表
                    if (pollCount % 10 === 0) {
                        loadData();
                    }
                }

            } catch (error) {
                console.error(`轮询批次 ${batchId} 状态失败:`, error);
                
                // 如果是网络错误，继续轮询几次
                if (pollCount < 5) {
                    return; // 继续轮询
                }
                
                // 多次失败后停止轮询
                clearInterval(pollInterval);
                message.warning({ 
                    content: `无法获取批次 "${batchName}" 的状态，请手动刷新查看`, 
                    key: `polling_${batchId}`,
                    duration: 5
                });
            }
        }, 5000); // 每5秒轮询一次
    };

    // 处理批次冲突
    const handleBatchConflict = (conflictData, formValues) => {
        const existingBatch = conflictData.existing_batch;
        
        Modal.confirm({
            title: '发现相同时间范围的批次',
            width: 600,
            content: (
                <div style={{ marginTop: 16 }}>
                    <Alert
                        message="已存在相同时间范围的批次"
                        description="创建新批次将删除现有批次及其所有相关数据，包括航班利润记录和异常记录。"
                        type="warning"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                    
                    <Descriptions title="现有批次信息" size="small" column={1} bordered>
                        <Descriptions.Item label="批次名称">{existingBatch.batch_name}</Descriptions.Item>
                        <Descriptions.Item label="批次类型">{existingBatch.type}</Descriptions.Item>
                        <Descriptions.Item label="时间范围">
                            {existingBatch.start_date} ~ {existingBatch.end_date}
                        </Descriptions.Item>
                        <Descriptions.Item label="批次状态">
                            <Badge 
                                status={existingBatch.status === 'COMPLETED' ? 'success' : 
                                       existingBatch.status === 'RUNNING' ? 'processing' : 'default'} 
                                text={existingBatch.status} 
                            />
                        </Descriptions.Item>
                        <Descriptions.Item label="总成本">¥{existingBatch.total_cost.toLocaleString()}</Descriptions.Item>
                        <Descriptions.Item label="航班数量">{existingBatch.total_flights} 个航班</Descriptions.Item>
                        <Descriptions.Item label="创建时间">{existingBatch.created_at}</Descriptions.Item>
                        {existingBatch.completed_at && (
                            <Descriptions.Item label="完成时间">{existingBatch.completed_at}</Descriptions.Item>
                        )}
                    </Descriptions>
                    
                    <div style={{ marginTop: 16, color: '#ff4d4f', fontSize: '14px' }}>
                        <strong>⚠️ 警告：</strong>选择"替换现有批次"将：
                        <ul style={{ marginTop: 8, paddingLeft: 16 }}>
                            <li>永久删除上述批次的所有数据</li>
                            <li>删除所有关联的航班利润记录</li>
                            <li>删除所有航线匹配异常记录</li>
                            <li>此操作不可撤销</li>
                        </ul>
                    </div>
                </div>
            ),
            okText: '替换现有批次',
            okButtonProps: { danger: true },
            cancelText: '取消',
            onOk: () => {
                // 用户确认替换，重新提交带replace_existing=true的请求
                message.loading({ content: '正在替换现有批次...', key: 'batch', duration: 0 });
                handleSubmit(formValues, true);
            },
            onCancel: () => {
                // 用户取消，保持modal开启，但重置submitting状态
                setSubmitting(false);
            }
        });
    };

    // 表格列定义
    const columns = [
        {
            title: '批次ID',
            dataIndex: 'batch_id',
            key: 'batch_id',
            width: 80,
            align: 'center',
        },
        {
            title: '批次名称',
            dataIndex: 'batch_name',
            key: 'batch_name',
            width: 250,
            align: 'center',
            ellipsis: true,
        },
        {
            title: '类型',
            dataIndex: 'type',
            key: 'type',
            width: 100,
            align: 'center',
            render: (type) => <BatchTypeTag type={type} />
        },
        {
            title: '时间范围',
            key: 'date_range',
            width: 200,
            align: 'center',
            render: (_, record) => {
                const formatDateStr = (dateStr) => {
                    if (!dateStr) return '';
                    // 处理 ISO 格式日期，只取 YYYY-MM-DD 部分
                    return dateStr.split('T')[0];
                };
                return (
                    <span>{formatDateStr(record.start_date)} 至 {formatDateStr(record.end_date)}</span>
                );
            }
        },
        {
            title: '状态',
            dataIndex: 'status',
            key: 'status',
            width: 100,
            align: 'center',
            render: (status) => <BatchStatusBadge status={status} size="small" showIcon={false} />
        },
        {
            title: '航班数',
            dataIndex: 'total_flights',
            key: 'total_flights',
            width: 80,
            align: 'center',
        },
        {
            title: '完整航线',
            dataIndex: 'total_complete_routes',
            key: 'total_complete_routes',
            width: 90,
            align: 'center',
        },
        {
            title: '不完整航线',
            dataIndex: 'total_incomplete_routes',
            key: 'total_incomplete_routes',
            width: 110,
            align: 'center',
            render: (value, record) => {
                if (value <= 0) return value;
                const pnb = record.pending_next_batch_count || 0;
                const hasCrossMonthOnly = record.status === 'COMPLETED' && pnb > 0 && pnb >= (record.total_anomalies_count || value);
                if (hasCrossMonthOnly) {
                    return (
                        <Tooltip title="均为跨月待确认，下期自动处理">
                            <span style={{ color: '#bfbfbf' }}>{value}</span>
                        </Tooltip>
                    );
                }
                return <span style={{ color: '#fa8c16' }}>{value}</span>;
            }
        },
        {
            title: '未匹配航班',
            dataIndex: 'total_unmatched_flights',
            key: 'total_unmatched_flights',
            width: 110,
            align: 'center',
            render: (value, record) => {
                if (value <= 0) return value;
                const pnb = record.pending_next_batch_count || 0;
                const hasCrossMonthOnly = record.status === 'COMPLETED' && pnb > 0 && pnb >= (record.total_anomalies_count || value);
                if (hasCrossMonthOnly) {
                    return (
                        <Tooltip title="均为跨月待确认，下期自动处理">
                            <span style={{ color: '#bfbfbf' }}>{value}</span>
                        </Tooltip>
                    );
                }
                return <span style={{ color: '#ff4d4f' }}>{value}</span>;
            }
        },
        {
            title: '涉及飞机',
            dataIndex: 'aircraft_regs',
            key: 'aircraft_regs',
            width: 140,
            align: 'center',
            render: (regs) => {
                if (!regs || regs.length === 0) return '-';
                if (regs.length <= 2) return regs.map(r => <Tag key={r} color="geekblue" style={{ margin: 2 }}>{r}</Tag>);
                return (
                    <Tooltip title={regs.join(', ')}>
                        <Space size={2} wrap>
                            <Tag color="geekblue">{regs[0]}</Tag>
                            <Tag>+{regs.length - 1}</Tag>
                        </Space>
                    </Tooltip>
                );
            }
        },
        {
            title: '总成本 (USD)',
            dataIndex: 'total_cost',
            key: 'total_cost',
            width: 130,
            align: 'center',
            render: (value) => `$${Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        },
        {
            title: '创建时间',
            dataIndex: 'created_at',
            key: 'created_at',
            width: 160,
            align: 'center',
            render: (time) => {
                if (!time) return '-';
                const date = new Date(time);
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                const hours = String(date.getHours()).padStart(2, '0');
                const minutes = String(date.getMinutes()).padStart(2, '0');
                const seconds = String(date.getSeconds()).padStart(2, '0');
                return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
            }
        },
        {
            title: '操作',
            key: 'action',
            width: 200,
            fixed: 'right',
            render: (_, record) => (
                <Space size="small">
                    <Button 
                        type="link" 
                        size="small" 
                        icon={<EyeOutlined />}
                        onClick={() => handleView(record)}
                    >
                        查看
                    </Button>
                    {(record.status === 'COMPLETED' || record.status === 'NEEDS_RECALCULATION') && (
                        <Button 
                            type="link" 
                            size="small" 
                            icon={<SyncOutlined />}
                            onClick={() => handleRefresh(record)}
                        >
                            刷新
                        </Button>
                    )}
                    <Popconfirm
                        title="确定要删除这个批次吗？"
                        description="删除后将无法恢复，所有相关记录都将被删除。"
                        onConfirm={() => handleDelete(record.batch_id)}
                        okText="确定"
                        cancelText="取消"
                    >
                        <Button 
                            type="link" 
                            size="small" 
                            danger
                            icon={<DeleteOutlined />}
                        >
                            删除
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    // 表格分页配置
    const handleTableChange = (newPagination) => {
        loadData(newPagination.current, newPagination.pageSize);
    };

    return (
        <div className="page-container">
            {/* 页面标题 */}
            <div className="page-header-compact">
                <div className="page-title-compact">批次核算管理</div>
                <Space>
                    <Button 
                        icon={<ReloadOutlined />} 
                        onClick={() => loadData()}
                    >
                        刷新
                    </Button>
                    <Button 
                        type="primary" 
                        icon={<PlusOutlined />}
                        onClick={handleCreate}
                    >
                        创建批次
                    </Button>
                </Space>
            </div>

            {/* 筛选区域 */}
            <Card className="filter-card">
                <Row gutter={16}>
                    <Col span={6}>
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <span>批次类型</span>
                            <Select
                                style={{ width: '100%' }}
                                placeholder="全部类型"
                                allowClear
                                value={filters.type}
                                onChange={(value) => setFilters({ ...filters, type: value })}
                            >
                                <Option value="OFFICIAL">正式</Option>
                                <Option value="DRAFT">草稿</Option>
                                <Option value="ADJUSTMENT">调整</Option>
                                <Option value="TEST">测试</Option>
                            </Select>
                        </Space>
                    </Col>
                    <Col span={6}>
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <span>批次状态</span>
                            <Select
                                style={{ width: '100%' }}
                                placeholder="全部状态"
                                allowClear
                                value={filters.status}
                                onChange={(value) => setFilters({ ...filters, status: value })}
                            >
                                <Option value="PENDING">待处理</Option>
                                <Option value="RUNNING">运行中</Option>
                                <Option value="COMPLETED">已完成</Option>
                                <Option value="FAILED">失败</Option>
                                <Option value="NEEDS_RECALCULATION">需要重新计算</Option>
                            </Select>
                        </Space>
                    </Col>
                    <Col span={6}>
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <span>飞机号</span>
                            <Select
                                style={{ width: '100%' }}
                                placeholder="全部飞机"
                                allowClear
                                showSearch
                                value={filters.aircraft_reg}
                                onChange={(value) => setFilters({ ...filters, aircraft_reg: value })}
                                options={(Array.isArray(aircraftOptions) ? aircraftOptions : []).map(reg => ({ label: reg, value: reg }))}
                            />
                        </Space>
                    </Col>
                </Row>
            </Card>

            {/* 数据表格 */}
            <Card className="data-card">
                <Table
                    size="small"
                    columns={columns}
                    dataSource={dataSource}
                    rowKey="batch_id"
                    loading={loading}
                    pagination={{
                        ...pagination,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total) => `共 ${total} 条记录`,
                    }}
                    onChange={handleTableChange}
                    scroll={{ x: 1800 }}
                    expandable={{
                        expandedRowRender: (record) => {
                            const children = expandedChildren[record.batch_id];
                            if (loadingChildren[record.batch_id]) return <div style={{ padding: 16, textAlign: 'center' }}>加载子批次中...</div>;
                            if (!children || children.length === 0) return <div style={{ padding: 16 }}>无子批次</div>;
                            return (
                                <Table
                                    size="small"
                                    columns={[
                                        { title: '子批次ID', dataIndex: 'batch_id', width: 80, align: 'center' },
                                        { title: '飞机号', dataIndex: 'aircraft_reg', width: 100, align: 'center', render: (v) => v ? <Tag color="geekblue">{v}</Tag> : '-' },
                                        { title: '状态', dataIndex: 'status', width: 100, align: 'center', render: (s) => <BatchStatusBadge status={s} size="small" showIcon={false} /> },
                                        { title: '航班数', dataIndex: 'total_flights', width: 80, align: 'center' },
                                        { title: '完整航线', dataIndex: 'total_complete_routes', width: 90, align: 'center' },
                                        { title: '不完整航线', dataIndex: 'total_incomplete_routes', width: 100, align: 'center', render: (v) => v > 0 ? <span style={{ color: '#fa8c16' }}>{v}</span> : v },
                                        { title: '总成本', dataIndex: 'total_cost', width: 120, align: 'center', render: (v) => `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` },
                                        { title: '操作', width: 120, render: (_, child) => (
                                            <Space size="small">
                                                <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => handleView(child)}>查看</Button>
                                                {(child.status === 'COMPLETED' || child.status === 'NEEDS_RECALCULATION') && (
                                                    <Button type="link" size="small" icon={<SyncOutlined />} onClick={() => handleRefresh(child)}>刷新</Button>
                                                )}
                                            </Space>
                                        )},
                                    ]}
                                    dataSource={children}
                                    rowKey="batch_id"
                                    pagination={false}
                                    style={{ margin: '-8px 0' }}
                                />
                            );
                        },
                        rowExpandable: (record) => record.has_children,
                        onExpand: (expanded, record) => {
                            if (expanded && record.has_children) loadChildren(record.batch_id);
                        },
                    }}
                />
            </Card>

            {/* 创建批次弹窗 */}
            <Modal
                title="创建批次核算"
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                footer={null}
                width={800}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    initialValues={{
                        type: 'OFFICIAL',
                        date_range: [dayjs().startOf('month'), dayjs().endOf('month')]
                    }}
                >
                    <Form.Item
                        label="批次名称"
                        name="batch_name"
                        rules={[
                            { required: true, message: '请输入批次名称' },
                            { min: 3, message: '批次名称至少3个字符' },
                            { max: 255, message: '批次名称不能超过255个字符' }
                        ]}
                    >
                        <Input 
                            placeholder="例如: 2024-10月度结算-最终版" 
                            disabled={submitting}
                        />
                    </Form.Item>

                    <Form.Item
                        label="批次类型"
                        name="type"
                        rules={[{ required: true, message: '请选择批次类型' }]}
                        tooltip="正式: 正式结算批次 | 草稿: 试算批次 | 调整: 调整之前批次 | 测试: 测试用"
                    >
                        <Select placeholder="请选择批次类型" disabled={submitting}>
                            <Option value="OFFICIAL">正式</Option>
                            <Option value="DRAFT">草稿</Option>
                            <Option value="ADJUSTMENT">调整</Option>
                            <Option value="TEST">测试</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label="时间范围"
                        name="date_range"
                        rules={[{ required: true, message: '请选择时间范围' }]}
                        tooltip="基于航班实际起飞时间(ATD)进行筛选"
                    >
                        <RangePicker 
                            style={{ width: '100%' }}
                            format="YYYY-MM-DD"
                            disabled={submitting}
                        />
                    </Form.Item>

                    <Form.Item
                        label="批次说明"
                        name="description"
                    >
                        <TextArea 
                            rows={3} 
                            placeholder="可选: 输入批次说明信息" 
                            disabled={submitting}
                        />
                    </Form.Item>

                    <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
                        <Space>
                            <Button onClick={() => setModalVisible(false)} disabled={submitting}>
                                取消
                            </Button>
                            <Button type="primary" htmlType="submit" loading={submitting}>
                                创建
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default BatchCalculationListPage;

